import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'user' | string; // Allow custom roles
  permissions?: string[]; // Add permissions array
  is_impersonating?: boolean;
  original_super_admin_id?: string;
}

export interface Client {
  id: string;
  name: string;
  domain: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  selectedClient: Client | null;
  availableClients: Client[];
  isAuthenticated: boolean;
  // Super admin impersonation
  originalUser: User | null; // Store original super admin user
  isImpersonating: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  setSelectedClient: (client: Client | null) => void;
  setAvailableClients: (clients: Client[]) => void;
  setImpersonation: (impersonationData: {
    originalUser: User;
    impersonatedUser: User;
    selectedClient: Client;
  }) => void;
  clearImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      selectedClient: null,
      availableClients: [],
      isAuthenticated: false,
      originalUser: null,
      isImpersonating: false,
      login: (user: User, token: string) =>
        set({ user, token, isAuthenticated: true }),
      logout: () =>
        set({
          user: null,
          token: null,
          selectedClient: null,
          availableClients: [],
          isAuthenticated: false,
          originalUser: null,
          isImpersonating: false,
        }),
      setSelectedClient: (client: Client | null) =>
        set({ selectedClient: client }),
      setAvailableClients: (clients: Client[]) =>
        set({ availableClients: clients }),
      setImpersonation: (impersonationData) =>
        set({
          originalUser: impersonationData.originalUser,
          user: impersonationData.impersonatedUser,
          selectedClient: impersonationData.selectedClient,
          isImpersonating: true,
        }),
      clearImpersonation: () => {
        const { originalUser } = get();
        set({
          user: originalUser,
          originalUser: null,
          selectedClient: null,
          isImpersonating: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        selectedClient: state.selectedClient,
        availableClients: state.availableClients,
        isAuthenticated: state.isAuthenticated,
        originalUser: state.originalUser,
        isImpersonating: state.isImpersonating,
      }),
    }
  )
);
